import React from 'react';

import { useIntl } from 'react-intl';

import ArrowUpRightOpenIcon from '@atlaskit/icon/core/arrow-up-right';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';

import { AiBrand } from '../../../../../constants';
import { messages } from '../../../../../messages';
import { useAIAnswerContext } from '../../../../ai-answer-context';
import { onWhoPrimaryViewProfileClick } from '../../../../on-who-primary-view-profile-click';
import { onWhoPrimaryViewProfileContextMenuClick } from '../../../../on-who-primary-view-profile-context-menu-click';

import { ProfileSectionPrimaryUserHeader } from './profile-section-primary-user-header';
import { ProfileSectionPrimaryUserImg } from './profile-section-primary-user-img';
import { ProfileSectionPrimaryUserInfoTextWrapper } from './profile-section-primary-user-info-text-wrapper';
import { ProfileSectionPrimaryUserInfoWrapper } from './profile-section-primary-user-info-wrapper';
import { ProfileSectionPrimaryUserName } from './profile-section-primary-user-name';
import { ProfileSectionPrimaryUserWrapper } from './profile-section-primary-user-wrapper';
import { ProfileSectionPrimaryViewProfileLinkWrapper } from './profile-section-primary-view-profile-link-wrapper';
import { ProfileSectionPrimaryWrapper } from './profile-section-primary-wrapper';
import { ProfileSectionUserInfo } from './user-info';
import { UserInfoTypeFormat } from './user-info-type-format';

type ProfileSectionProps = {
	accountId?: string;
	name: string;
	email?: string | null;
	avatarUrl?: string | null;
	profileUrl?: string | null;
	jobTitle?: string | null;
	department?: string | null;
	location?: string | null;
	time?: string | null;
};

export const ProfileSection = ({
	accountId,
	name,
	email,
	avatarUrl,
	profileUrl,
	jobTitle,
	department,
	location,
	time,
}: ProfileSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { commonAttributes, fireAnalyticsEvent, onNavigate } = useAIAnswerContext();

	const profileLink =
		commonAttributes.brand === AiBrand.AI ? `/wiki/people/${accountId}` : profileUrl;

	return (
		<ProfileSectionPrimaryWrapper>
			<ProfileSectionPrimaryUserWrapper>
				<ProfileSectionPrimaryUserImg src={avatarUrl || ''} alt={name} />
				<ProfileSectionPrimaryUserInfoWrapper>
					<ProfileSectionPrimaryUserHeader>
						<ProfileSectionPrimaryUserName>{name}</ProfileSectionPrimaryUserName>
						<ProfileSectionPrimaryViewProfileLinkWrapper>
							<ArrowUpRightOpenIcon
								spacing="spacious"
								label=""
								color={token('color.text.accent.blue')}
							/>
							<Link
								href={profileLink || ''}
								onClick={(event) => {
									onNavigate(event);
									fireAnalyticsEvent(
										onWhoPrimaryViewProfileClick({
											...commonAttributes,
											primaryUserName: name,
										}),
									);
								}}
								onContextMenu={(event) => {
									onNavigate(event);
									fireAnalyticsEvent(
										onWhoPrimaryViewProfileContextMenuClick({
											...commonAttributes,
											primaryUserName: name,
										}),
									);
								}}
							>
								{formatMessage(messages.ai_answer_who_questions_view_profile)}
							</Link>
						</ProfileSectionPrimaryViewProfileLinkWrapper>
					</ProfileSectionPrimaryUserHeader>
					{/* if job title and department are missing, don't need this row */}
					{(jobTitle || department) && (
						<ProfileSectionPrimaryUserInfoTextWrapper>
							<ProfileSectionUserInfo
								userInfo={jobTitle || ''}
								userInfoType={UserInfoTypeFormat.JOBTITLE}
							/>
							<ProfileSectionUserInfo
								userInfo={department || ''}
								userInfoType={UserInfoTypeFormat.DEPARTMENT}
							/>
						</ProfileSectionPrimaryUserInfoTextWrapper>
					)}
					{/* primary user will always at least have email */}
					<ProfileSectionPrimaryUserInfoTextWrapper>
						<ProfileSectionUserInfo
							userInfo={location || ''}
							userInfoType={UserInfoTypeFormat.LOCATION}
						/>
						<ProfileSectionUserInfo
							userInfo={time || ''}
							userInfoType={UserInfoTypeFormat.TIME}
							applyAdditionalTopIconPadding
						/>
						<ProfileSectionUserInfo
							userInfo={email || ''}
							userInfoType={UserInfoTypeFormat.EMAIL}
							applyAdditionalTopIconPadding
						/>
					</ProfileSectionPrimaryUserInfoTextWrapper>
				</ProfileSectionPrimaryUserInfoWrapper>
			</ProfileSectionPrimaryUserWrapper>
		</ProfileSectionPrimaryWrapper>
	);
};
